import React, { useCallback, useMemo, useRef, useState } from 'react';
import ISchema from '../../models/ISchema';
import Guid from '../../models/Guid';

export interface IUniteSelectionPlan {
  idUnite: Guid,
  description: string,
}

interface ICellulePlan {
  ligne: number,
  colonne: number,
}

interface IPayloadGlisser {
  idUnite: string,
  description: string,
}

interface ISchemaSitePlanCanvasProps {
  details: ISchema[],
  largeurBouton: number,
  hauteurBouton: number,
  espacementBouton: number,
  selectedUnite: IUniteSelectionPlan | null,
  selectedDetail?: ISchema | null,
  readOnly: boolean,
  onPlace: (draft: IUniteSelectionPlan & ICellulePlan) => void,
  onMove: (detail: ISchema, cellule: ICellulePlan) => void,
  onSelectDetail: (detail: ISchema | null) => void,
}

const estMemeCellule = (detail: ISchema, cellule: ICellulePlan) =>
  detail.ligne === cellule.ligne && detail.colonne === cellule.colonne;

const lirePayload = (event: React.DragEvent<HTMLDivElement>): IPayloadGlisser | null => {
  try {
    const payload = JSON.parse(event.dataTransfer.getData('text/plain') || '');
    if (!payload?.idUnite) {
      return null;
    }

    return {
      idUnite: payload.idUnite,
      description: payload.description,
    };
  } catch {
    return null;
  }
};

export default function SchemaSitePlanCanvas({
  details,
  largeurBouton,
  hauteurBouton,
  espacementBouton,
  selectedUnite,
  selectedDetail,
  readOnly,
  onPlace,
  onMove,
  onSelectDetail,
}: ISchemaSitePlanCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const deplacementRef = useRef<{
    detail: ISchema,
    pointerId: number,
    startX: number,
    startY: number,
    dragging: boolean,
  } | null>(null);
  const ignorerClicRef = useRef(false);
  const [celluleSurvol, setCelluleSurvol] = useState<ICellulePlan | null>(null);
  const [detailDeplace, setDetailDeplace] = useState<ISchema | null>(null);

  const largeur = Math.max(0, largeurBouton || 0);
  const hauteur = Math.max(0, hauteurBouton || 0);
  const espacement = Math.max(0, espacementBouton || 0);
  const pasX = Math.max(1, largeur + espacement);
  const pasY = Math.max(1, hauteur + espacement);

  const idsUnitesPlacees = useMemo(
    () => new Set(details.map((detail) => detail.idUnite?.toString())),
    [details]
  );

  const uniteDejaPlacee = Boolean(selectedUnite && idsUnitesPlacees.has(selectedUnite.idUnite.toString()));
  const enPlacement = Boolean(!readOnly && selectedUnite && !uniteDejaPlacee && !detailDeplace);
  const enDeplacement = Boolean(!readOnly && detailDeplace);

  const celluleOccupee = useCallback((cellule: ICellulePlan, ignorer?: ISchema | null) => (
    details.some((detail) =>
      estMemeCellule(detail, cellule)
      && !detail.idSchemaSiteDetail?.equals(ignorer?.idSchemaSiteDetail)
    )
  ), [details]);

  const descriptionApercu = detailDeplace?.description || selectedUnite?.description;
  const peutAfficherApercu = Boolean(
    !readOnly
    && descriptionApercu
    && celluleSurvol
    && (enDeplacement || enPlacement)
    && !celluleOccupee(celluleSurvol, detailDeplace)
  );

  const extraireCellule = useCallback((clientX: number, clientY: number): ICellulePlan | null => {
    const cible = canvasRef.current;
    if (!cible) {
      return null;
    }

    const rect = cible.getBoundingClientRect();
    const x = clientX - rect.left + cible.scrollLeft;
    const y = clientY - rect.top + cible.scrollTop;

    return {
      colonne: Math.max(0, Math.floor(x / pasX)),
      ligne: Math.max(0, Math.floor(y / pasY)),
    };
  }, [pasX, pasY]);

  const mettreAJourSurvol = useCallback((clientX: number, clientY: number) => {
    const deplacementEnCours = Boolean(deplacementRef.current?.dragging);
    if (readOnly || (!enPlacement && !deplacementEnCours)) {
      setCelluleSurvol(null);
      return;
    }

    setCelluleSurvol(extraireCellule(clientX, clientY));
  }, [enPlacement, extraireCellule, readOnly]);

  const onMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (deplacementRef.current) {
      return;
    }

    mettreAJourSurvol(event.clientX, event.clientY);
  }, [mettreAJourSurvol]);

  const onMouseLeave = useCallback(() => {
    if (deplacementRef.current) {
      return;
    }

    setCelluleSurvol(null);
  }, []);

  const placerSurCellule = useCallback((cellule: ICellulePlan) => {
    const detailExistant = details.find((detail) => estMemeCellule(detail, cellule));
    if (detailExistant) {
      onSelectDetail(detailExistant);
      return;
    }

    if (!selectedUnite || uniteDejaPlacee) {
      onSelectDetail(null);
      return;
    }

    onPlace({
      idUnite: selectedUnite.idUnite,
      description: selectedUnite.description,
      ligne: cellule.ligne,
      colonne: cellule.colonne,
    });
  }, [details, onPlace, onSelectDetail, selectedUnite, uniteDejaPlacee]);

  const onCanvasClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (ignorerClicRef.current) {
      ignorerClicRef.current = false;
      return;
    }

    if (readOnly) {
      onSelectDetail(null);
      return;
    }

    const cellule = extraireCellule(event.clientX, event.clientY);
    if (!cellule) {
      return;
    }

    placerSurCellule(cellule);
  }, [extraireCellule, onSelectDetail, placerSurCellule, readOnly]);

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (readOnly) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = detailDeplace ? 'move' : 'copy';
    mettreAJourSurvol(event.clientX, event.clientY);
  }, [detailDeplace, mettreAJourSurvol, readOnly]);

  const onDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    const cible = event.relatedTarget as Node | null;
    if (cible && event.currentTarget.contains(cible)) {
      return;
    }

    setCelluleSurvol(null);
  }, []);

  const onDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (readOnly) {
      return;
    }

    const cellule = extraireCellule(event.clientX, event.clientY);
    if (!cellule) {
      return;
    }

    const payload = lirePayload(event);
    let uniteAPlacer = selectedUnite;
    if (!uniteAPlacer && payload?.idUnite) {
      uniteAPlacer = {
        idUnite: new Guid(payload.idUnite),
        description: payload.description,
      };
    }

    if (!uniteAPlacer || idsUnitesPlacees.has(uniteAPlacer.idUnite.toString()) || celluleOccupee(cellule)) {
      setCelluleSurvol(null);
      return;
    }

    onPlace({
      idUnite: uniteAPlacer.idUnite,
      description: uniteAPlacer.description,
      ligne: cellule.ligne,
      colonne: cellule.colonne,
    });
    setCelluleSurvol(null);
  }, [celluleOccupee, extraireCellule, idsUnitesPlacees, onPlace, readOnly, selectedUnite]);

  const estDansLeCanvas = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return false;
    }

    const rect = canvas.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }, []);

  const terminerDeplacement = useCallback((clientX: number, clientY: number) => {
    const deplacement = deplacementRef.current;
    deplacementRef.current = null;
    setDetailDeplace(null);
    setCelluleSurvol(null);

    if (!deplacement?.dragging) {
      return;
    }

    ignorerClicRef.current = true;
    if (!estDansLeCanvas(clientX, clientY)) {
      return;
    }

    const cellule = extraireCellule(clientX, clientY);
    if (!cellule || celluleOccupee(cellule, deplacement.detail)) {
      return;
    }

    onMove(deplacement.detail, cellule);
  }, [celluleOccupee, estDansLeCanvas, extraireCellule, onMove]);

  const onBoutonClick = useCallback((event: React.MouseEvent<HTMLDivElement>, detail: ISchema) => {
    event.stopPropagation();
    if (ignorerClicRef.current) {
      ignorerClicRef.current = false;
      return;
    }

    onSelectDetail(detail);
  }, [onSelectDetail]);

  const onBoutonPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>, detail: ISchema) => {
    if (readOnly || event.button !== 0) {
      return;
    }

    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    deplacementRef.current = {
      detail,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      dragging: false,
    };
    onSelectDetail(detail);
  }, [onSelectDetail, readOnly]);

  const onBoutonPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const deplacement = deplacementRef.current;
    if (!deplacement || deplacement.pointerId !== event.pointerId) {
      return;
    }

    const distance = Math.hypot(event.clientX - deplacement.startX, event.clientY - deplacement.startY);
    if (!deplacement.dragging) {
      if (distance < 4) {
        return;
      }

      deplacement.dragging = true;
      setDetailDeplace(deplacement.detail);
    }

    event.preventDefault();
    mettreAJourSurvol(event.clientX, event.clientY);
  }, [mettreAJourSurvol]);

  const onBoutonPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const deplacement = deplacementRef.current;
    if (!deplacement || deplacement.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    terminerDeplacement(event.clientX, event.clientY);
  }, [terminerDeplacement]);

  const maxColonne = Math.max(0, ...details.map((detail) => detail.colonne), celluleSurvol?.colonne ?? 0);
  const maxLigne = Math.max(0, ...details.map((detail) => detail.ligne), celluleSurvol?.ligne ?? 0);

  const styleBouton = useCallback((ligne: number, colonne: number): React.CSSProperties => ({
    left: colonne * pasX,
    top: ligne * pasY,
    width: largeur,
    height: hauteur,
  }), [hauteur, largeur, pasX, pasY]);

  const classesCanvas = [
    'gestion-schemas-site-form-plan-canvas',
    enPlacement ? 'gestion-schemas-site-form-plan-canvas--placement' : '',
    enDeplacement ? 'gestion-schemas-site-form-plan-canvas--deplacement' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={canvasRef}
      className={classesCanvas}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onCanvasClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div
        className={"gestion-schemas-site-form-plan-canvas-inner"}
        style={{
          width: (maxColonne + 2) * pasX,
          height: (maxLigne + 2) * pasY,
        }}
      >
        {details.map((detail) => {
          const estSelectionne = Boolean(selectedDetail?.idSchemaSiteDetail?.equals(detail.idSchemaSiteDetail));
          const estEnDeplacement = Boolean(detailDeplace?.idSchemaSiteDetail?.equals(detail.idSchemaSiteDetail));
          return (
            <div
              key={detail.idSchemaSiteDetail?.toString()}
              className={`gestion-schemas-site-form-plan-bouton${estSelectionne ? ' gestion-schemas-site-form-plan-bouton--selected' : ''}${!readOnly ? ' gestion-schemas-site-form-plan-bouton--deplacer' : ''}${estEnDeplacement ? ' gestion-schemas-site-form-plan-bouton--en-deplacement' : ''}`}
              style={styleBouton(detail.ligne, detail.colonne)}
              onClick={(event) => onBoutonClick(event, detail)}
              onPointerDown={(event) => onBoutonPointerDown(event, detail)}
              onPointerMove={onBoutonPointerMove}
              onPointerUp={onBoutonPointerUp}
              onPointerCancel={onBoutonPointerUp}
            >
              {detail.description}
            </div>
          );
        })}
        {peutAfficherApercu && celluleSurvol && descriptionApercu ? (
          <div
            className={"gestion-schemas-site-form-plan-bouton gestion-schemas-site-form-plan-bouton--preview"}
            style={styleBouton(celluleSurvol.ligne, celluleSurvol.colonne)}
          >
            {descriptionApercu}
          </div>
        ) : null}
      </div>
    </div>
  );
}
